import { createSelector } from "@reduxjs/toolkit"
import { asyncAction } from "../common"
import { limsRequest } from "./limsRequest"

// Sample-prep worklist templates (F9, SPEC-041 / PRD-008; BE companion SPEC-024). A team-scoped,
// VERSIONED catalogue of reusable sample-prep worklist templates, one lineage per analysis
// definition (the ordered preparation steps a lab follows when preparing a sample for an analysis —
// e.g. an HPLC-assay prep: weigh, dissolve, sonicate, dilute, filter, transfer to vial, store). A
// lab leader curates the template in one place; the analyst instantiates it per sample at run time
// (limsSampleWorklists) and signs it off.
//
// Versioned change control (GMP traceability) — the SAME reviewer→supervisor e-sign pattern as the
// SST templates (limsSstTemplates) and forskrifter (limsForskrifter): a template is never edited in
// place. `createVersion` inserts a new DRAFT version; it becomes the LIVE (effective) option only
// once a reviewer e-signs it (draft → reviewer-approved) and a supervisor e-signs it
// (reviewer-approved → effective; prior effective → superseded). The WorklistModal + the
// send-to-supervisor/QA gate use the EFFECTIVE version (`state === "effective"`).
//
// Persisted via /lims/worklist-templates (GET open to any LIMS user; POST + POST /<id> gated
// server-side as capability-admin; the two approve sub-routes are e-signed). With showLims off this
// slice is inert (no worklist UI mounts).

// SPEC-024 / F7 — the worklist-template version state machine (mirrors the backend
// WORKLIST_TEMPLATE_STATES and the SST / forskrift state machine). Only an EFFECTIVE version is
// live for the per-sample worklist + the gate.
export const WORKLIST_TEMPLATE_STATES = {
  DRAFT: "draft",
  REVIEWER_APPROVED: "reviewer-approved",
  EFFECTIVE: "effective",
  SUPERSEDED: "superseded",
}

const initialState = { items: [], isLoading: false }

// Shared action type for both create() and createVersion() (a new version is just another insert,
// so it folds into the same reducer case — one place to maintain the append logic). The reducer
// replaces a same-id row if present (the just-superseded one re-arrives via a later fetch anyway)
// and otherwise appends.
const ADD_ACTION = "limsWorklistTemplates/add"

export function reducer(state = initialState, action) {
  switch (action.type) {
    case "limsWorklistTemplates/fetch/BEGIN":
      return { ...state, isLoading: true }
    case "limsWorklistTemplates/fetch":
      return { items: action.res, isLoading: false }
    case ADD_ACTION: {
      const item = action.res
      if (!item) return state
      const exists = state.items.some((x) => x.id === item.id)
      return {
        ...state,
        items: exists ? state.items.map((x) => (x.id === item.id ? item : x)) : [...state.items, item],
      }
    }
    case "auth/logout":
      return initialState
    default:
      return state
  }
}

// ---- Thunks ----------------------------------------------------------------------------------

export const fetch = asyncAction("limsWorklistTemplates/fetch", (_dispatch) => {
  return limsRequest("GET", "/lims/worklist-templates").then((res) => res.items)
})

// Create version 1 of a new worklist template (capability-admin only; 403 otherwise). `template`
// carries name + analysisDefinitionId + header + steps. SPEC-024 / F7: the new v1 starts as a DRAFT
// — it becomes live only after the reviewer→supervisor e-sign. Returns the created (v1, draft) record.
export const create = asyncAction(ADD_ACTION, (_dispatch, template) => {
  return limsRequest("POST", "/lims/worklist-templates", { input: template }).then((res) => res.item)
})

// Create the NEXT version of an existing template as a DRAFT (admin only). `id` may be any version
// in the lineage; the server inserts the bump in `draft` and does NOT supersede the current live
// version (it stays usable while the revision is reviewed — SPEC-024 / F7). `template` carries the
// new content (the admin pre-fills from the version being edited). Returns the new draft version.
export const createVersion = asyncAction(ADD_ACTION, (_dispatch, id, template) => {
  return limsRequest("POST", `/lims/worklist-templates/${id}`, { input: template }).then((res) => res.item)
})

// Reviewer-approves a draft template version: draft → reviewer-approved. The signer's password +
// reason ride at the request-body TOP LEVEL; the server enforces the "reviewer" capability + 4-eyes
// (≠ the version creator). (SPEC-024 / F7.) Returns the updated version.
export const reviewerApprove = asyncAction(ADD_ACTION, (_dispatch, id, signature) => {
  return limsRequest("POST", `/lims/worklist-templates/${id}/reviewer-approve`, {
    password: (signature && signature.password) || undefined,
    reason: (signature && signature.reason) || undefined,
  }).then((res) => res.item)
})

// Supervisor-approves a reviewer-approved template version: → effective; the server supersedes any
// prior effective version of the same lineage in the same transaction. "supervisor" capability,
// 4-eyes (≠ the version creator AND the reviewer signer). (SPEC-024 / F7.) Returns the updated
// version; a subsequent `fetch()` reflects the superseded prior version.
export const supervisorApprove = asyncAction(ADD_ACTION, (_dispatch, id, signature) => {
  return limsRequest("POST", `/lims/worklist-templates/${id}/supervisor-approve`, {
    password: (signature && signature.password) || undefined,
    reason: (signature && signature.reason) || undefined,
  }).then((res) => res.item)
})

// ---- Selectors -------------------------------------------------------------------------------

export const selectWorklistTemplates = (state) => state.limsWorklistTemplates.items

// A single template (any version) by id, or undefined.
export const selectTemplate = (state, id) =>
  id ? state.limsWorklistTemplates.items.find((x) => x.id === id) : undefined

// Every template version for one analysis definition (any state), name + version sorted — the admin
// register expands each analysis row to its templates. Returns a fresh array; call sites select per
// definition (one row at a time) or shallow-compare. A generic example template (null definition)
// is not returned for a specific definition.
export const selectTemplatesForDefinition = (state, defId) => {
  if (!defId) return []
  return state.limsWorklistTemplates.items
    .filter((t) => t.analysisDefinitionId === defId)
    .sort((a, b) => {
      const n = (a.name || "").localeCompare(b.name || "")
      return n !== 0 ? n : (a.versionNumber || 0) - (b.versionNumber || 0)
    })
}

// The single EFFECTIVE *analysis-specific* worklist template for a definition, or null. Memoised so
// the modal doesn't re-render on unrelated state changes. Specific-only — it never returns a generic
// (null-definition) template. This is the building block for both the FILL selector (which adds a
// generic fallback on top) and the GATE boolean (which must stay specific-only).
const selectSpecificEffectiveMemo = createSelector(
  [(state) => state.limsWorklistTemplates.items, (_state, defId) => defId],
  (items, defId) => {
    if (!defId) return null
    return (
      items.find((t) => t.analysisDefinitionId === defId && t.state === WORKLIST_TEMPLATE_STATES.EFFECTIVE) || null
    )
  }
)

// The single EFFECTIVE generic (null-definition) worklist template, or null — the FILL fallback (the
// seeded HPLC example). Memoised once (no per-definition arg) so it stays a stable reference.
const selectGenericEffectiveMemo = createSelector(
  [(state) => state.limsWorklistTemplates.items],
  (items) =>
    items.find((t) => t.analysisDefinitionId == null && t.state === WORKLIST_TEMPLATE_STATES.EFFECTIVE) || null
)

// The effective worklist template the WorklistModal instantiates (FILL): the analysis-specific one,
// else a fall back to the GENERIC effective template (analysisDefinitionId null) so the analyst
// still gets a usable checklist when the analysis has no template of its own. Mirrors the backend
// `effective_or_generic_template_for_definition`. NOT the gate — see selectHasEffectiveTemplate.
export const selectEffectiveForDefinition = (state, defId) =>
  selectSpecificEffectiveMemo(state, defId) || selectGenericEffectiveMemo(state)

// Whether an analysis definition has an effective worklist template OF ITS OWN — the cheap boolean
// the ResultEntryPanel send-to-supervisor/QA gate reads. SPECIFIC-ONLY by design: it must NOT become
// true via the generic fallback (a generic template must never blanket-gate every analysis), so it
// reads the specific-only selector directly rather than the now-fallback'ing selectEffectiveForDefinition.
export const selectHasEffectiveTemplate = (state, defId) => selectSpecificEffectiveMemo(state, defId) !== null
